import { Page, Text, View, Document, StyleSheet, Font } from "@react-pdf/renderer";
import persianFont from "../../../../assets/fonts/YekanBakh/woff/YekanBakh-Regular.woff";
import persianFontBold from "../../../../assets/fonts/YekanBakh/woff/YekanBakh-Bold.woff";
import { convertToJalali } from "../../../../utils/date";
import { HotelIcon, RoomIcon, InfoIcon, AmenitiesIcon, RemarkIcon, RateIcon, PoliciesIcon } from "../../../../assets/svg-components/SvgComponents";

const IntAccommodationPdf = ({ orderItem }) => {
    Font.register({
        family: "iranSans",
        fonts: [
            { src: persianFont, fontWeight: "normal" },
            { src: persianFontBold, fontWeight: "bold" },
        ],
    });

    const styles = StyleSheet.create({
        page: {
            direction: "rtl",
            padding: "40px",
            fontFamily: "iranSans",
            fontSize: 10,
            color: "#333",
            backgroundColor: "#f9f9f9",
        },
        header: {
            flexDirection: "row-reverse",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 30,
            borderBottom: "2px solid #666",
            paddingBottom: 20,
        },
        voucherTitle: {
            fontSize: 28,
            fontWeight: "bold",
            color: "#333",
        },
        boldText: {
            fontWeight: "bold",
            fontSize: 12,
        },
        text: {
            fontSize: 10,
            marginBottom: 5,
        },
        section: {
            marginBottom: 20,
        },
        sectionHeader: {
            flexDirection: "row",
            alignItems: "center",
            marginBottom: 10,
        },
        sectionIcon: {
            width: 20,
            height: 20,
            marginRight: 10,
        },
        title: {
            backgroundColor: "#006363",
            color: "white",
            padding: "8px 15px",
            fontWeight: "bold",
            borderRadius: 3,
            flex: 1,
        },
        content: {
            padding: "10px 15px",
            backgroundColor: "white",
            borderRadius: 3,
            border: "1px solid #ccc",
        },
        row: {
            flexDirection: "row-reverse",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
        },
        label: {
            border: "1px solid #666",
            borderRadius: 15,
            padding: "3px 8px",
            color: "#666",
        },
        passengers: {
            marginBottom: 10,
            padding: "8px 12px",
            backgroundColor: "#f0f0f0",
            borderRadius: 3,
        },
        rateInfo: {
            backgroundColor: "#f0f0f0",
            padding: "10px 15px",
            marginBottom: 10,
            border: "1px solid #ccc",
            borderRadius: 3,
        },
        importantInfo: {
            color: "#333",
            fontSize: 10,
            fontWeight: "bold",
            padding: "5px 10px",
            backgroundColor: "#e0e0e0",
            borderRadius: 3,
        },
        importantInfo2: {
            color: "#333",
            fontSize: 10,
            fontWeight: "bold",
            padding: "5px 10px",
            backgroundColor: "#d0d0d0",
            borderRadius: 3,
        },
        policySection: {
            marginBottom: 8,
        },
        policyTitle: {
            fontWeight: "bold",
            marginBottom: 3,
            color: "#444",
        },
        policyText: {
            fontSize: 9,
            marginBottom: 3,
        },
    });

    const renderHtmlContent = htmlContent => {
        if (!htmlContent) return null;

        // Remove all HTML tags and replace with line breaks
        const cleanContent = htmlContent
            .replace(/<\/?[^>]+(>|$)/g, "\n")
            .split("\n")
            .map(line => line.trim())
            .filter(Boolean);

        let currentTitle = null;
        const groupedContent = [];

        cleanContent.forEach(line => {
            // Assume a line is a title if it's short and doesn't end with punctuation
            if (line.length < 30 && !line.match(/[.!?]$/)) {
                currentTitle = line;
                groupedContent.push({ title: currentTitle, content: [] });
            } else {
                if (groupedContent.length === 0) {
                    groupedContent.push({ title: null, content: [] });
                }
                groupedContent[groupedContent.length - 1].content.push(line);
            }
        });

        return groupedContent.map((group, index) => (
            <View key={index} style={styles.policySection}>
                {group.title && <Text style={styles.policyTitle}>{group.title}</Text>}
                {group.content.map((line, lineIndex) => (
                    <Text key={lineIndex} style={styles.policyText}>
                        • {line}
                    </Text>
                ))}
            </View>
        ));
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <View>
                        <Text style={styles.text}>Tracking Code: {orderItem.tracking_code}</Text>
                        <Text style={styles.text}>Issue Date: {convertToJalali(orderItem.create_date)}</Text>
                    </View>
                    <Text style={styles.voucherTitle}>Hotel Voucher</Text>
                </View>

                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <div style={styles.sectionIcon}>
                            <HotelIcon />
                        </div>

                        <Text style={styles.title}>Hotel Information</Text>
                    </View>
                    <View style={styles.content}>
                        <View style={styles.row}>
                            <Text style={styles.boldText}>{orderItem.accommodation.accommodation.english_name}</Text>
                            <Text style={styles.label}>{orderItem.accommodation.accommodation.star || "N/A"} Star</Text>
                        </View>
                        <Text style={styles.text}>Address: {orderItem.accommodation.accommodation.english_address}</Text>
                        <Text style={styles.text}>Check-in: {orderItem.accommodation.check_in}</Text>
                        <Text style={styles.text}>Check-out: {orderItem.accommodation.check_out}</Text>
                        <Text style={styles.text}>Confirmation Code: {orderItem.accommodation.confirmation_code}</Text>
                    </View>
                </View>

                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <div style={styles.sectionIcon}>
                            <RoomIcon />
                        </div>

                        <Text style={styles.title}>Rooms Information</Text>
                    </View>
                    {orderItem.accommodation.rooms.map((room, index) => (
                        <View key={index} style={styles.content}>
                            <View style={styles.row}>
                                <Text style={styles.boldText}>{room.name}</Text>
                                <Text style={styles.text}>Meal Plan: {room.meal_plan}</Text>
                                <Text style={styles.label}>{room.type || "Room"}</Text>
                            </View>
                            <Text style={styles.text}>Adults: {room.adult_count}</Text>
                            {room.childrens && <Text style={styles.text}>childrens: {room.childrens}</Text>}
                            {room.passengers.map((passenger, pIndex) => (
                                <View key={pIndex} style={styles.passengers}>
                                    <Text style={styles.boldText}>
                                        Passenger {pIndex + 1}: {passenger.first_name} {passenger.last_name}, {passenger.title}
                                    </Text>
                                </View>
                            ))}
                        </View>
                    ))}
                </View>

                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <div style={styles.sectionIcon}>
                            <RateIcon />
                        </div>

                        <Text style={styles.title}>Rate Information</Text>
                    </View>
                    <View style={styles.rateInfo}>
                        <Text style={styles.text}>Excluded: {orderItem.accommodation.extra_charge.excluded}</Text>
                        <Text style={styles.text}>Included: {orderItem.accommodation.extra_charge.included || "N/A"}</Text>
                        <Text style={styles.text}>Meal Plan: {orderItem.accommodation.extra_charge.meal_plan_desc}</Text>
                    </View>
                </View>
                {
                    orderItem?.accommodation?.amenities?.length &&
                    <View style={styles.section}>
                        <View style={styles.sectionHeader}>
                            <div style={styles.sectionIcon}>
                                <AmenitiesIcon />
                            </div>

                            <Text style={styles.title}>Amenities</Text>
                        </View>
                        <View style={styles.content}>
                            {orderItem.accommodation.amenities.map((amenity, index) => (
                                <Text key={index} style={styles.text}>
                                    • {amenity.amenity}
                                </Text>
                            ))}
                        </View>
                    </View>
                }

                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <div style={styles.sectionIcon}>
                            <RemarkIcon />
                        </div>

                        <Text style={styles.title}>Remarks</Text>
                    </View>
                    <View style={styles.content}>
                        {orderItem.accommodation.remarks.map((remark, index) => (
                            <Text key={index} style={styles.text}>
                                • {remark.remark.replace(/<[^>]*>/g, "")}
                            </Text>
                        ))}
                    </View>
                </View>

                {orderItem.accommodation.policies.know_before_you_go && (
                    <View style={styles.section}>
                        <View style={styles.sectionHeader}>
                            <div style={styles.sectionIcon}>
                                <InfoIcon />
                            </div>

                            <Text style={styles.title}>Important Information</Text>
                        </View>
                        <Text style={styles.importantInfo}>{renderHtmlContent(orderItem.accommodation.policies.know_before_you_go)}</Text>
                    </View>
                )}
                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <div style={styles.sectionIcon}>
                            <InfoIcon />
                        </div>
                        <Text style={styles.title}>Note:</Text>
                    </View>
                    <Text style={styles.importantInfo2}>Early checkout is full night no show!</Text>
                </View>
                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <div style={styles.sectionIcon}>
                            <PoliciesIcon />
                        </div>

                        <Text style={styles.title}>Hotel Policies</Text>
                    </View>
                    <View style={styles.content}>
                        {orderItem.accommodation.policies.times && (
                            <>
                                <Text style={styles.text}>Check-in time: {orderItem.accommodation.policies.times.begin || "N/A"}</Text>
                                <Text style={styles.text}>Check-out time: {orderItem.accommodation.policies.times.check_out || "N/A"}</Text>
                            </>
                        )}
                        {orderItem.accommodation.policies.min_age && (
                            <Text style={styles.text}>Minimum age: {orderItem.accommodation.policies.min_age}</Text>
                        )}
                        {orderItem.accommodation.policies.instructions && (
                            <Text style={styles.text}>Instructions: {renderHtmlContent(orderItem.accommodation.policies.instructions)}</Text>
                        )}
                        {orderItem.accommodation.policies.special_instructions && (
                            <>
                                <Text style={styles.policyTitle}>Special Instructions:</Text>
                                {renderHtmlContent(orderItem.accommodation.policies.special_instructions)}
                            </>
                        )}
                        {orderItem.accommodation.policies.child_policy_desc_fa && (
                            <Text style={styles.text}>Child Policy: {orderItem.accommodation.policies.child_policy_desc_fa}</Text>
                        )}
                        {orderItem.accommodation.policies.single_woman_desc_fa && (
                            <Text style={styles.text}>Single Woman Policy: {orderItem.accommodation.policies.single_woman_desc_fa}</Text>
                        )}

                        {orderItem.accommodation.policies.pet_attributes && orderItem.accommodation.policies.pet_attributes.length > 0 && (
                            <>
                                <Text style={styles.policyTitle}>Pet Policy:</Text>
                                {orderItem.accommodation.policies.pet_attributes.map((attr, index) => (
                                    <Text key={index} style={styles.policyText}>
                                        • {attr.attribute}
                                    </Text>
                                ))}
                            </>
                        )}
                        {orderItem.accommodation.policies.fees && (
                            <>
                                {orderItem.accommodation.policies.fees.mandatory && (
                                    <Text style={styles.text}>
                                        Mandatory Fees: {renderHtmlContent(orderItem.accommodation.policies.fees.mandatory)}
                                    </Text>
                                )}
                                {orderItem.accommodation.policies.fees.optional && (
                                    <Text style={styles.text}>
                                        Optional Fees: {renderHtmlContent(orderItem.accommodation.policies.fees.optional)}
                                    </Text>
                                )}
                            </>
                        )}
                        {orderItem.accommodation.policies.payment_detail && (
                            <>
                                <Text style={styles.policyTitle}>Payment Details:</Text>
                                {renderHtmlContent(orderItem.accommodation.policies.payment_detail)}
                            </>
                        )}
                        {orderItem.accommodation.policies.license_number && (
                            <Text style={styles.text}>License Number: {orderItem.accommodation.policies.license_number}</Text>
                        )}
                        {orderItem.accommodation.policies.key_collection_info && (
                            <Text style={styles.text}>Key Collection Info: {orderItem.accommodation.policies.key_collection_info}</Text>
                        )}
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default IntAccommodationPdf;
